export interface Note {
  freq: number;       // Hz
  startSec: number;   // when to start, relative to loop start
  durationSec: number;
  gain: number;       // 0..1
  wave?: 'triangle' | 'square' | 'sine'; // oscillator type; default = triangle
}

// A-minor scale frequencies used throughout
const A4  = 440.00;
const C5  = 523.25;
const D5  = 587.33;
const E5  = 659.25;
const F5  = 698.46;
const G5  = 783.99;
const A5  = 880.00;
const B4  = 493.88;
const C6  = 1046.50;
const E4  = 329.63;
const G4  = 392.00;

// Quarter note at ~130 bpm ≈ 0.46s, eighth ≈ 0.23s, half ≈ 0.92s
// 30 s / 0.46 s ≈ 65 quarter-notes max budget — we aim for ≥ 60 notes total
// Section A: bars 1-8, 0-15s (melody only, 30 notes)
// Section B: 15-30s (same melody backbone repeated + octave overlay, 30+ notes)

// ── Section A (0–15 s) ─────────────────────────────────────────
// 8 bars × ~1.85 s each ≈ 14.8 s.  Simple stepwise A-minor melody.
const SECTION_A_NOTES: Note[] = [
  // Bar 1 (0.00 – 1.84)
  { freq: A4, startSec: 0.00, durationSec: 0.40, gain: 0.40 },
  { freq: C5, startSec: 0.46, durationSec: 0.40, gain: 0.38 },
  { freq: E5, startSec: 0.92, durationSec: 0.80, gain: 0.42 },

  // Bar 2 (1.84 – 3.68)
  { freq: D5, startSec: 1.84, durationSec: 0.40, gain: 0.38 },
  { freq: E5, startSec: 2.30, durationSec: 0.40, gain: 0.40 },
  { freq: G5, startSec: 2.76, durationSec: 0.80, gain: 0.40 },

  // Bar 3 (3.68 – 5.52)
  { freq: A5, startSec: 3.68, durationSec: 0.40, gain: 0.42 },
  { freq: G5, startSec: 4.14, durationSec: 0.40, gain: 0.38 },
  { freq: E5, startSec: 4.60, durationSec: 0.80, gain: 0.40 },

  // Bar 4 (5.52 – 7.36)
  { freq: D5, startSec: 5.52, durationSec: 0.40, gain: 0.38 },
  { freq: C5, startSec: 5.98, durationSec: 0.40, gain: 0.38 },
  { freq: A4, startSec: 6.44, durationSec: 0.80, gain: 0.42 },

  // Bar 5 (7.36 – 9.20)
  { freq: E5, startSec: 7.36, durationSec: 0.40, gain: 0.40 },
  { freq: F5, startSec: 7.82, durationSec: 0.40, gain: 0.38 },
  { freq: G5, startSec: 8.28, durationSec: 0.80, gain: 0.40 },

  // Bar 6 (9.20 – 11.04)
  { freq: A5, startSec: 9.20, durationSec: 0.40, gain: 0.42 },
  { freq: G5, startSec: 9.66, durationSec: 0.40, gain: 0.38 },
  { freq: F5, startSec: 10.12, durationSec: 0.40, gain: 0.38 },
  { freq: E5, startSec: 10.58, durationSec: 0.40, gain: 0.40 },

  // Bar 7 (11.04 – 12.88)
  { freq: D5, startSec: 11.04, durationSec: 0.40, gain: 0.38 },
  { freq: E5, startSec: 11.50, durationSec: 0.40, gain: 0.40 },
  { freq: C5, startSec: 11.96, durationSec: 0.80, gain: 0.38 },

  // Bar 8 (12.88 – 14.72) — cadence back to A
  { freq: B4, startSec: 12.88, durationSec: 0.40, gain: 0.36 },
  { freq: A4, startSec: 13.34, durationSec: 0.40, gain: 0.40 },
  { freq: G4, startSec: 13.80, durationSec: 0.40, gain: 0.38 },
  { freq: A4, startSec: 14.26, durationSec: 0.80, gain: 0.42 },
];

// ── Section B (15–30 s) ────────────────────────────────────────
// Same 8-bar backbone shifted +15s, plus an octave-doubled layer (gain 0.15).
// Together they add 26 melody notes + 13 octave-overlay notes = 39 notes in B.
const BACKBONE_B: Note[] = [
  // Bar 1 (15.00 – 16.84)
  { freq: A4, startSec: 15.00, durationSec: 0.40, gain: 0.40 },
  { freq: C5, startSec: 15.46, durationSec: 0.40, gain: 0.38 },
  { freq: E5, startSec: 15.92, durationSec: 0.80, gain: 0.42 },

  // Bar 2 (16.84 – 18.68)
  { freq: D5, startSec: 16.84, durationSec: 0.40, gain: 0.38 },
  { freq: E5, startSec: 17.30, durationSec: 0.40, gain: 0.40 },
  { freq: G5, startSec: 17.76, durationSec: 0.80, gain: 0.40 },

  // Bar 3 (18.68 – 20.52)
  { freq: A5, startSec: 18.68, durationSec: 0.40, gain: 0.42 },
  { freq: G5, startSec: 19.14, durationSec: 0.40, gain: 0.38 },
  { freq: E5, startSec: 19.60, durationSec: 0.80, gain: 0.40 },

  // Bar 4 (20.52 – 22.36)
  { freq: D5, startSec: 20.52, durationSec: 0.40, gain: 0.38 },
  { freq: C5, startSec: 20.98, durationSec: 0.40, gain: 0.38 },
  { freq: A4, startSec: 21.44, durationSec: 0.80, gain: 0.42 },

  // Bar 5 (22.36 – 24.20)
  { freq: E5, startSec: 22.36, durationSec: 0.40, gain: 0.40 },
  { freq: F5, startSec: 22.82, durationSec: 0.40, gain: 0.38 },
  { freq: G5, startSec: 23.28, durationSec: 0.80, gain: 0.40 },

  // Bar 6 (24.20 – 26.04)
  { freq: A5, startSec: 24.20, durationSec: 0.40, gain: 0.42 },
  { freq: G5, startSec: 24.66, durationSec: 0.40, gain: 0.38 },
  { freq: F5, startSec: 25.12, durationSec: 0.40, gain: 0.38 },
  { freq: E5, startSec: 25.58, durationSec: 0.40, gain: 0.40 },

  // Bar 7 (26.04 – 27.88)
  { freq: D5, startSec: 26.04, durationSec: 0.40, gain: 0.38 },
  { freq: E5, startSec: 26.50, durationSec: 0.40, gain: 0.40 },
  { freq: C5, startSec: 26.96, durationSec: 0.80, gain: 0.38 },

  // Bar 8 (27.88 – 29.72)
  { freq: B4, startSec: 27.88, durationSec: 0.40, gain: 0.36 },
  { freq: A4, startSec: 28.34, durationSec: 0.40, gain: 0.40 },
  { freq: G4, startSec: 28.80, durationSec: 0.40, gain: 0.38 },
  { freq: A4, startSec: 29.26, durationSec: 0.70, gain: 0.42 },
];

// Octave-doubled overlay for every other bar in Section B (sparse = subtle variation)
// Covers bars 1, 3, 5, 7 of Section B — 13 notes at gain 0.15
const OCTAVE_OVERLAY: Note[] = [
  // Bar 1 overlay (A5, C6, E6≈1318.5)
  { freq: A5,   startSec: 15.00, durationSec: 0.40, gain: 0.15 },
  { freq: C6,   startSec: 15.46, durationSec: 0.40, gain: 0.15 },
  { freq: E5 * 2, startSec: 15.92, durationSec: 0.80, gain: 0.13 },

  // Bar 3 overlay
  { freq: A5 * 2, startSec: 18.68, durationSec: 0.40, gain: 0.12 },
  { freq: G5,   startSec: 19.14, durationSec: 0.40, gain: 0.14 },
  { freq: A5,   startSec: 19.60, durationSec: 0.80, gain: 0.15 },

  // Bar 5 overlay
  { freq: A5,   startSec: 22.36, durationSec: 0.40, gain: 0.14 },
  { freq: C6,   startSec: 22.82, durationSec: 0.40, gain: 0.15 },
  { freq: A5,   startSec: 23.28, durationSec: 0.80, gain: 0.14 },

  // Bar 7 overlay
  { freq: G5,   startSec: 26.04, durationSec: 0.40, gain: 0.13 },
  { freq: A5,   startSec: 26.50, durationSec: 0.40, gain: 0.15 },
  { freq: E4 * 2, startSec: 26.96, durationSec: 0.80, gain: 0.14 },
  // Final tag
  { freq: A5,   startSec: 29.26, durationSec: 0.70, gain: 0.15 },
];

const SECTION_B_NOTES: Note[] = [...BACKBONE_B, ...OCTAVE_OVERLAY];

export const BATTLE_LOOP_NOTES: Note[] = [...SECTION_A_NOTES, ...SECTION_B_NOTES];
export const LOOP_DURATION_SEC = 30;
